/**
 * Header Manager System
 *
 * Sistema modular para gerenciamento de navegação.
 * Segue princípios SOLID para código manutenível e reutilizável.
 *
 * @version 2.0.0
 * @author Wicomm
 *
 * Arquitetura:
 * ┌─────────────────────────────────────────────────────────┐
 * │                    HeaderManager                         │
 * │                   (Facade/Mediator)                      │
 * ├─────────────────────────────────────────────────────────┤
 * │  - Orquestra componentes via eventos                    │
 * │  - Fecha outros ao abrir um                             │
 * └──────────────┬────────────────────┬─────────────────────┘
 *                │                    │
 *     ┌──────────▼──────────┐  ┌──────▼──────────┐
 *     │   DropdownManager    │  │   DrawerManager  │
 *     │  (Desktop hover)     │  │  (Mobile slide)  │
 *     └─────────────────────┘  └──────────────────┘
 */

/* ==========================================================================
   EVENT BUS
   Sistema de eventos desacoplado para comunicação entre componentes
   ========================================================================== */

/**
 * EventBus - Padrão Observer para comunicação desacoplada
 *
 * Princípios:
 * - SRP: Apenas gerencia eventos
 * - OCP: Novos eventos sem modificar código existente
 * - DIP: Componentes dependem da abstração (eventos), não de implementações
 */
class EventBus {
  constructor() {
    this.listeners = new Map();
  }

  /**
   * Registra um listener para um evento
   * @param {string} event - Nome do evento
   * @param {Function} callback - Função a ser executada
   * @returns {Function} Função para remover o listener
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);

    // Retorna função de cleanup
    return () => this.off(event, callback);
  }

  /**
   * Remove um listener
   * @param {string} event - Nome do evento
   * @param {Function} callback - Função a ser removida
   */
  off(event, callback) {
    this.listeners.get(event)?.delete(callback);
  }

  /**
   * Emite um evento
   * @param {string} event - Nome do evento
   * @param {*} data - Dados do evento
   */
  emit(event, data = {}) {
    this.listeners.get(event)?.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`[EventBus] Error in listener for "${event}":`, error);
      }
    });
  }
}

// Instância global do EventBus
const headerEvents = new EventBus();

/* ==========================================================================
   DROPDOWN MANAGER
   Gerenciamento de menus dropdown desktop com hover delay
   ========================================================================== */

/**
 * DropdownManager - Responsabilidade Única: Gerenciar dropdowns desktop
 *
 * Features:
 * - Hover com delay (previne abertura acidental)
 * - Delay no fechamento (permite mover para o menu)
 * - Suporte a teclado (focus/blur)
 * - Backdrop dinâmico
 */
class DropdownManager {
  /**
   * @param {Object} config - Configurações
   * @param {string} config.containerSelector - Seletor do container dos triggers
   * @param {string} config.triggerSelector - Seletor dos triggers
   * @param {string} config.menuSelector - Seletor dos menus
   * @param {string} config.menuAttribute - Atributo que liga trigger ao menu
   * @param {number} config.openDelay - Delay para abrir (ms)
   * @param {number} config.closeDelay - Delay para fechar (ms)
   * @param {string} config.openClass - Classe quando aberto
   * @param {string} config.activeClass - Classe do trigger ativo
   */
  constructor(config = {}) {
    this.config = {
      containerSelector: '[data-dropdown-container]',
      triggerSelector: '[data-dropdown-trigger]',
      menuSelector: '[data-dropdown-menu]',
      menuAttribute: 'data-dropdown-menu',
      openDelay: 150,
      closeDelay: 200,
      openClass: 'is-open',
      activeClass: 'is-active',
      ...config
    };

    this.container = null;
    this.triggers = [];
    this.menus = [];
    this.activeMenu = null;
    this.activeTrigger = null;
    this.openTimeout = null;
    this.closeTimeout = null;

    this._init();
  }

  /**
   * Inicialização
   * @private
   */
  _init() {
    this.container = document.querySelector(this.config.containerSelector);
    if (!this.container) return;

    this.triggers = [...document.querySelectorAll(this.config.triggerSelector)];
    this.menus = [...document.querySelectorAll(this.config.menuSelector)];

    this._bindEvents();
  }

  /**
   * Vincula eventos
   * @private
   */
  _bindEvents() {
    // Eventos nos triggers
    this.triggers.forEach(trigger => {
      trigger.addEventListener('mouseenter', () => this._handleTriggerEnter(trigger));
      trigger.addEventListener('mouseleave', () => this._handleTriggerLeave());
      trigger.addEventListener('focus', () => this._handleTriggerEnter(trigger));
      trigger.addEventListener('blur', () => this._handleTriggerLeave());
    });

    // Eventos no conteúdo dos menus (para manter aberto quando mouse está no conteúdo)
    // Se o mouse sair do conteúdo mas ainda estiver no background, o menu fecha
    this.menus.forEach(menu => {
      const content = menu.querySelector('[data-dropdown-content]');
      if (content) {
        content.addEventListener('mouseenter', () => this._cancelClose());
        content.addEventListener('mouseleave', () => this._handleTriggerLeave());
      } else {
        // Fallback: se não tiver conteúdo separado, usa o menu inteiro
        menu.addEventListener('mouseenter', () => this._cancelClose());
        menu.addEventListener('mouseleave', () => this._handleTriggerLeave());
      }
    });

    // ESC fecha o menu
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeMenu) {
        this.closeAll();
      }
    });

    // Escuta evento global para fechar quando outro componente abrir
    headerEvents.on('drawer:open', () => this.closeAll());
  }

  /**
   * Handler para mouse/focus entrar no trigger
   * @private
   */
  _handleTriggerEnter(trigger) {
    const menuId = trigger.dataset.dropdownTrigger;
    const menu = document.querySelector(`[data-dropdown-menu="${menuId}"]`);

    if (!menu) return;

    this._cancelClose();
    clearTimeout(this.openTimeout);

    this.openTimeout = setTimeout(() => {
      // Fecha menu ativo se diferente
      if (this.activeMenu && this.activeMenu !== menu) {
        this._hideMenu(this.activeMenu);
        this._deactivateTrigger();
      }

      this._showMenu(menu);
      this._activateTrigger(trigger);
      this.activeMenu = menu;
      this.activeTrigger = trigger;

      // Emite evento
      headerEvents.emit('dropdown:open', { menu, trigger });
    }, this.config.openDelay);
  }

  /**
   * Handler para mouse/focus sair do trigger
   * @private
   */
  _handleTriggerLeave() {
    clearTimeout(this.openTimeout);

    this.closeTimeout = setTimeout(() => {
      this.closeAll();
    }, this.config.closeDelay);
  }

  /**
   * Cancela o fechamento
   * @private
   */
  _cancelClose() {
    clearTimeout(this.closeTimeout);
  }

  /**
   * Mostra um menu
   * @private
   */
  _showMenu(menu) {
    menu.classList.add(this.config.openClass);
    menu.setAttribute('aria-hidden', 'false');
  }

  /**
   * Esconde um menu
   * @private
   */
  _hideMenu(menu) {
    menu.classList.remove(this.config.openClass);
    menu.setAttribute('aria-hidden', 'true');
  }

  /**
   * Ativa visualmente o trigger
   * @private
   */
  _activateTrigger(trigger) {
    this.triggers.forEach(t => t.classList.remove(this.config.activeClass));
    trigger.classList.add(this.config.activeClass);
  }

  /**
   * Desativa todos os triggers
   * @private
   */
  _deactivateTrigger() {
    this.triggers.forEach(t => t.classList.remove(this.config.activeClass));
  }

  /**
   * Fecha todos os menus
   */
  closeAll() {
    clearTimeout(this.openTimeout);
    clearTimeout(this.closeTimeout);

    if (this.activeMenu) {
      this._hideMenu(this.activeMenu);
      this._deactivateTrigger();

      headerEvents.emit('dropdown:close', { menu: this.activeMenu });

      this.activeMenu = null;
      this.activeTrigger = null;
    }
  }

  /**
   * Destrói a instância
   */
  destroy() {
    this.closeAll();
  }
}

/* ==========================================================================
   DRAWER MANAGER
   Gerenciamento de drawer mobile (menu hamburguer)
   ========================================================================== */

/**
 * DrawerManager - Responsabilidade Única: Gerenciar drawer mobile
 *
 * Features:
 * - Abre/fecha com animação
 * - Overlay clicável
 * - Bloqueia scroll do body
 * - Focus trap para acessibilidade
 * - Fecha com ESC
 */
class DrawerManager {
  /**
   * @param {Object} config - Configurações
   * @param {string} config.drawerSelector - Seletor do drawer
   * @param {string} config.toggleSelector - Seletor do botão toggle
   * @param {string} config.closeSelector - Seletor do botão fechar
   * @param {string} config.overlaySelector - Seletor do overlay
   * @param {string} config.openClass - Classe quando aberto
   * @param {string} config.bodyOpenClass - Classe no body quando aberto
   */
  constructor(config = {}) {
    this.config = {
      drawerSelector: '[data-drawer]',
      toggleSelector: '[data-drawer-toggle]',
      closeSelector: '[data-drawer-close]',
      overlaySelector: '[data-drawer-overlay]',
      openClass: 'is-open',
      bodyOpenClass: 'drawer-open',
      ...config
    };

    this.drawer = null;
    this.overlay = null;
    this.toggleButtons = [];
    this.closeButtons = [];
    this.isOpen = false;
    this.previousActiveElement = null;

    // Flag para controle do history state (botao voltar mobile)
    this.historyStatePushed = false;

    // Bind do handler do popstate para poder remover depois
    this._boundPopStateHandler = this._handlePopState.bind(this);

    this._init();
  }

  /**
   * Inicialização
   * @private
   */
  _init() {
    this.drawer = document.querySelector(this.config.drawerSelector);
    if (!this.drawer) return;

    this.overlay = this.drawer.querySelector(this.config.overlaySelector);
    this.toggleButtons = [...document.querySelectorAll(this.config.toggleSelector)];
    this.closeButtons = [...this.drawer.querySelectorAll(this.config.closeSelector)];

    this._bindEvents();
  }

  /**
   * Vincula eventos
   * @private
   */
  _bindEvents() {
    // Toggle buttons
    this.toggleButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    });

    // Close buttons
    this.closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Overlay click
    this.overlay?.addEventListener('click', () => this.close());

    // ESC fecha
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Fecha ao redimensionar para desktop
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 990 && this.isOpen) {
        this.close();
      }
    });

    // Escuta evento global para fechar quando outro componente abrir
    headerEvents.on('dropdown:open', () => this.close());

    // Escuta o botao voltar do navegador (mobile)
    window.addEventListener('popstate', this._boundPopStateHandler);
  }

  /**
   * Handler para o evento popstate (botao voltar do navegador)
   * Fecha o drawer em vez de navegar para a pagina anterior
   * @private
   */
  _handlePopState() {
    if (this.isOpen && this.historyStatePushed) {
      // Evita que o close() faca history.back() novamente
      this.historyStatePushed = false;
      this._closeDrawer();
    }
  }

  /**
   * Abre o drawer
   */
  open() {
    if (!this.drawer || this.isOpen) return;

    // Salva elemento focado para restaurar depois
    this.previousActiveElement = document.activeElement;

    this.isOpen = true;
    this.drawer.classList.add(this.config.openClass);
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add(this.config.bodyOpenClass);
    document.body.style.overflow = 'hidden';

    // Foca no primeiro elemento focável
    requestAnimationFrame(() => {
      const firstFocusable = this.drawer.querySelector(
        'a[href], button:not([disabled]), [tabindex="0"]'
      );
      firstFocusable?.focus();
    });

    // Emite evento
    headerEvents.emit('drawer:open', { drawer: this });

    // Adiciona estado ao history para capturar o botao voltar (mobile)
    if (!this.historyStatePushed) {
      history.pushState({ drawerOpen: true }, '', window.location.href);
      this.historyStatePushed = true;
    }
  }

  /**
   * Fecha o drawer (metodo publico)
   * Se o history state foi adicionado, usa history.back() para manter consistencia
   */
  close() {
    if (!this.drawer || !this.isOpen) return;

    // Se adicionamos um state ao history, usamos back() para fechar
    // Isso vai disparar popstate -> _handlePopState -> _closeDrawer
    if (this.historyStatePushed) {
      history.back();
      return;
    }

    // Caso contrario, fecha diretamente
    this._closeDrawer();
  }

  /**
   * Fecha o drawer (logica interna)
   * @private
   */
  _closeDrawer() {
    if (!this.drawer) return;

    this.isOpen = false;
    this.drawer.classList.remove(this.config.openClass);
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(this.config.bodyOpenClass);
    document.body.style.overflow = '';

    // Restaura foco
    this.previousActiveElement?.focus();

    // Emite evento
    headerEvents.emit('drawer:close', { drawer: this });
  }

  /**
   * Alterna estado do drawer
   */
  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  /**
   * Fecha o drawer diretamente sem manipular o histórico
   * Usado quando outro componente (ex: SearchDrawerManager) precisa fechar o drawer
   * de forma programática sem disparar history.back()
   */
  forceClose() {
    if (!this.drawer || !this.isOpen) return;
    this.historyStatePushed = false;
    this._closeDrawer();
  }

  /**
   * Destrói a instância
   */
  destroy() {
    this.close();

    // Remove listener do popstate
    window.removeEventListener('popstate', this._boundPopStateHandler);
  }
}

/* ==========================================================================
   HEADER MANAGER
   Orquestrador principal (Facade Pattern)
   ========================================================================== */

/**
 * HeaderManager - Padrão Facade
 *
 * Coordena DropdownManager e DrawerManager.
 * Fornece API simples e unificada.
 *
 * Princípios:
 * - SRP: Apenas orquestra, não implementa lógica de componentes
 * - OCP: Novos componentes podem ser adicionados sem modificar existentes
 * - DIP: Depende de abstrações (eventos), não de implementações
 */
class HeaderManager {
  /**
   * @param {Object} config - Configurações
   * @param {Object} config.dropdown - Configurações do DropdownManager
   * @param {Object} config.drawer - Configurações do DrawerManager
   */
  constructor(config = {}) {
    this.config = config;
    this.dropdown = null;
    this.drawer = null;
    this.events = headerEvents;

    this._init();
  }

  /**
   * Inicialização
   * @private
   */
  _init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._setup());
    } else {
      this._setup();
    }
  }

  /**
   * Configura componentes
   * @private
   */
  _setup() {
    // Inicializa DropdownManager
    this.dropdown = new DropdownManager(this.config.dropdown);

    // Inicializa DrawerManager
    this.drawer = new DrawerManager(this.config.drawer);

    // Suporte ao Theme Editor
    this._bindThemeEditor();
  }

  /**
   * Vincula eventos do Theme Editor do Shopify
   * @private
   */
  _bindThemeEditor() {
    if (!window.Shopify?.designMode) return;

    document.addEventListener('shopify:section:load', (event) => {
      const isHeader = event.target.querySelector('[data-header]') ||
                       event.target.querySelector('[data-drawer]');
      if (isHeader) {
        this.destroy();
        this._setup();
      }
    });
  }

  /**
   * API: Abre o drawer
   */
  openDrawer() {
    this.drawer?.open();
  }

  /**
   * API: Fecha o drawer
   */
  closeDrawer() {
    this.drawer?.close();
  }

  /**
   * API: Alterna o drawer
   */
  toggleDrawer() {
    this.drawer?.toggle();
  }

  /**
   * API: Fecha todos os dropdowns
   */
  closeDropdowns() {
    this.dropdown?.closeAll();
  }

  /**
   * API: Registra listener para eventos
   * @param {string} event - Nome do evento (drawer:open, drawer:close, dropdown:open, dropdown:close)
   * @param {Function} callback - Função callback
   * @returns {Function} Função para remover listener
   */
  on(event, callback) {
    return this.events.on(event, callback);
  }

  /**
   * Destrói todas as instâncias
   */
  destroy() {
    this.dropdown?.destroy();
    this.drawer?.destroy();
  }
}

/* ==========================================================================
   INICIALIZAÇÃO
   ========================================================================== */

// Cria instância global
window.HeaderManager = new HeaderManager({
  dropdown: {
    containerSelector: '[data-header]',
    triggerSelector: '[data-dropdown-trigger]',
    menuSelector: '[data-dropdown-menu]'
  },
  drawer: {
    drawerSelector: '[data-drawer]',
    toggleSelector: '[data-drawer-toggle]',
    closeSelector: '[data-drawer-close]',
    overlaySelector: '[data-drawer-overlay]'
  }
});

// Export para módulos ES6
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { EventBus, DropdownManager, DrawerManager, HeaderManager };
}
